// GenCC gene–disease validity — presentation logic (TGCLOUD-1794, Phase 2).
//
// Pure, so the rules below are asserted directly rather than through a render.
// The component is a thin shell over this.
//
// ── THE FOUR STATES, AND WHY THERE ARE FOUR ───────────────────────────────
//
//   unavailable   we could not ask GenCC (not loaded, disabled, unreachable)
//   not-curated   GenCC answered, and has no submission for this gene
//   graded        one or more submitters, all saying the same thing
//   conflicting   submitters disagree
//
// `unavailable` and `not-curated` look identical in a naive implementation —
// both are "no grades to show" — and collapsing them is the defect this whole
// module is shaped around. "GenCC has no submission for this gene" is a
// finding about the evidence base; "we could not reach GenCC" is a fact about
// our infrastructure. Rendering the second as the first states something
// clinical on the strength of a network timeout. The same distinction is
// enforced server-side by backend/services/terminologyAvailability.js and in
// the gene→disease lookup, which was burned by exactly this.
//
// ── WHAT THIS MODULE REFUSES TO DO ────────────────────────────────────────
//
// It never reduces a pair's submitters to one grade. 39.5% of GenCC's
// gene–disease pairs carry more than one distinct classification and 9.7%
// straddle strong and weak evidence — ALDH4A1 / MONDO:0009401 is
// simultaneously Definitive, Strong, Limited and Supportive depending on who
// you ask. Picking a winner would manufacture a consensus that does not exist,
// which is the rule already written down for ClinVar at
// src/util/variantPathogenicity.js:8-41. `strongest()` exists only to ORDER
// rows on screen and is never presented as "the" grade.
//
// ── TWO VIEWS OF THE SAME ASSERTIONS ──────────────────────────────────────
//
//   diseases[]      one row per SUBMITTED-AS curie. Unchanged since Phase 2.
//   diseaseGroups[] one row per GenCC "disease equivalent" — the curies that
//                   share a MONDO id, which is how GenCC's own gene page
//                   counts diseases. Added in Phase 4 for the display.
//
// Both are returned. The grouped view is what the genotype record renders, and
// it is a STRICT SUPERSET of the ungrouped one by construction — see
// equivalentKeyOf, where the reason that matters (and the six genes that
// proved it) is written down. Anything that reads a conflict flag must read it
// from the same view as the `state` it sits beside, or two surfaces will
// contradict each other on the same data.

import { english } from 'src/i18n/language';

/** The eight GenCC classifications, strongest evidence first. */
export const GENCC_CLASSIFICATIONS = [
  'Definitive',
  'Strong',
  'Moderate',
  'Supportive',
  'Limited',
  'Disputed Evidence',
  'Refuted Evidence',
  'No Known Disease Relationship'
];

/**
 * A GenCC classification for display (TGCLOUD-1893): one of the eight above is
 * GenCC's fixed vocabulary and is translated; anything else is shown exactly as
 * GenCC sent it. Display only: every rule in this module reads the English.
 *
 * @param {string} classification
 * @param {Function} [t] useAutoTranslation's t; English when omitted
 */
export const classificationLabel = (classification, t = english) =>
  GENCC_CLASSIFICATIONS.includes(classification)
    ? t(classification)
    : classification;

// Rank for ORDERING ONLY. Not a score, not a threshold, and never compared
// across pairs to decide what a clinician should believe.
const RANK = new Map(GENCC_CLASSIFICATIONS.map((c, i) => [c, i]));
const rankOf = (c) =>
  RANK.has(c) ? RANK.get(c) : GENCC_CLASSIFICATIONS.length;

// Which side of the evidence line a classification sits on, used ONLY to
// detect a disagreement worth flagging. A pair where two submitters say
// Definitive and Strong is a difference of degree; one where they say
// Definitive and Refuted is a difference in kind, and the second is what a
// clinician needs pointed out.
const STRONG = new Set(['Definitive', 'Strong', 'Moderate']);
const WEAK = new Set([
  'Limited',
  'Disputed Evidence',
  'Refuted Evidence',
  'No Known Disease Relationship'
]);

// 'Supportive' is deliberately in NEITHER set. It is 16.7% of GenCC, it comes
// only from Orphanet, and it is Orphanet's sole value — an artefact of that
// submitter's process rather than a point on ClinGen's evidence scale. Ranking
// it is unavoidable (it has to sort somewhere); treating its presence as
// agreement OR disagreement with a ClinGen grade would be reading it as
// something it is not.
export const isUngradedScale = (classification) =>
  classification === 'Supportive';

export const GENE_VALIDITY_STATE = Object.freeze({
  UNIDENTIFIED_GENE: 'unidentified-gene',
  UNAVAILABLE: 'unavailable',
  NOT_CURATED: 'not-curated',
  GRADED: 'graded',
  CONFLICTING: 'conflicting'
});

// An approved HGNC symbol: a letter, then letters/digits/hyphens, no spaces and
// no punctuation. Mirrors backend/services/genotypeSymbol.js, deliberately as a
// separate copy — that one guards what is WRITTEN, this one guards what is
// LOOKED UP, and the two must be free to diverge without one silently loosening
// the other.
const LOOKS_LIKE_GENE_SYMBOL = /^[A-Za-z][A-Za-z0-9-]{0,20}$/;

/**
 * The symbol to look GenCC up by, or null when this record does not identify a
 * gene in a machine-readable way.
 *
 * MUST be given the record's STRUCTURED `symbol` field — never
 * getGenotypeDisplayParts().symbol, which is a DISPLAY helper that falls back to
 * the whole free-text `name`. Handing it 'BRCA1 c.5047G>T' returns a clean
 * "GenCC has no submission for this gene", which is a false clinical finding: we
 * never established which gene it is.
 *
 * The shape is re-checked here rather than trusted, because records written
 * before TGCLOUD-1830 can carry a free-text label in `symbol` itself. That makes
 * this safe on historic data with no migration.
 *
 * Integrations that legitimately record a gene outside HGNC nomenclature — the
 * Sapio import does exactly this — therefore get the "gene not identified"
 * state rather than a validity verdict they were never entitled to.
 */
export function geneLookupKey(genotype) {
  const raw =
    genotype && typeof genotype.symbol === 'string'
      ? genotype.symbol.trim()
      : '';
  if (!raw || !LOOKS_LIKE_GENE_SYMBOL.test(raw)) return null;
  return raw;
}

const EMPTY_TOTALS = Object.freeze({
  diseases: 0,
  submissions: 0,
  submitters: 0,
  conflicting: 0,
  hasUngraded: false
});

/**
 * The shape every return path starts from. `diseaseGroups: []` and a zeroed
 * `totals` are present even on the empty states, for the same reason
 * `diseases: []` is: a caller must never have to tell "no groups" apart from
 * "the field is missing on this state".
 */
function emptyBase(response) {
  return {
    datasetVersion: response?.datasetVersion ?? null,
    datasetDownloadedAt: response?.datasetDownloadedAt ?? null,
    gene: null,
    diseases: [],
    diseaseGroups: [],
    totals: { ...EMPTY_TOTALS }
  };
}

function geneOf(assertions) {
  const g = assertions.find((a) => a?.gene?.symbol)?.gene;
  return g ? { symbol: g.symbol, hgncId: g.hgncId ?? null } : null;
}

const submittedCodeOf = (a) =>
  a?.disease?.submittedAs || a?.disease?.code || '(unknown)';

/**
 * Submitted-as curies that map to MORE THAN ONE MONDO id within this gene.
 *
 * This set is what makes the grouping below safe, and it is not an edge case:
 * 119 (gene, curie) pairs in the 2026-09-20 release map to two MONDOs.
 */
function splitCuriesOf(assertions) {
  const seen = new Map();
  for (const a of assertions) {
    const mondo = a?.disease?.mondo;
    if (!mondo) continue;
    const curie = submittedCodeOf(a);
    if (!seen.has(curie)) seen.set(curie, new Set());
    seen.get(curie).add(mondo);
  }
  const split = new Set();
  for (const [curie, mondos] of seen) if (mondos.size > 1) split.add(curie);
  return split;
}

/**
 * Group an assertion into one of GenCC's own "disease equivalents".
 *
 * MONDO here is GenCC'S harmonisation, carried on the submission itself — NOT
 * a crosswalk of ours over disease_xrefs. That distinction is the whole reason
 * this is safe to do and the Phase 4 crosswalk was not: a broad xref is not an
 * equivalence assertion, but `disease_mondo` is what the submitter's record was
 * mapped to by GenCC, and grouping on it reproduces the 14,030 pair count
 * GenCC's own gene page shows as "Disease Equivalents".
 *
 * ── WHY THIS IS NOT SIMPLY `d.mondo` ──────────────────────────────────────
 * A naive MONDO key is NOT conflict-monotone. Because 119 curies map to more
 * than one MONDO, regrouping SPLITS rows as well as merging them, and a split
 * can pull the two halves of a disagreement apart. Measured on the live
 * release, six genes LOSE the "submitters disagree" flag they carry today:
 * ABCC8, CDAN1, MYH2, MYLK, SEC23B, SEMA3E.
 *
 * MYH2 is the worked case. OMIM:605637 today holds Strong from Ambry, Labcorp,
 * LMM and PanelApp Australia AND Limited from Illumina, so the row is flagged.
 * Keyed on MONDO alone, Illumina's Limited moves to MONDO:0019195 while the
 * strong side stays on MONDO:0011577 — neither group straddles, and the
 * disagreement vanishes from the screen. Silently deleting a disagreement flag
 * is the worst thing this surface could do.
 *
 * So: a curie that maps to more than one MONDO keeps its OWN row. Every merge
 * is then a strict SUPERSET of a per-curie row, which makes the flag monotone —
 * it can only ever appear, never vanish. Measured: 14,028 groups, 1,365
 * conflicting across 1,276 genes, against today's 1,274 / 1,192. 84 genes
 * gained, ZERO lost.
 */
function equivalentKeyOf(a, splitCuries) {
  const curie = submittedCodeOf(a);
  const mondo = a?.disease?.mondo;
  if (!mondo || splitCuries.has(curie)) return curie;
  return mondo;
}

/**
 * Uppercase the FIRST CHARACTER only — sentence case, never title case.
 *
 * 78.1% of MONDO's titles begin lowercase ('breast-ovarian cancer, familial,
 * susceptibility to, 1'), which reads as an inconsistency when stacked against
 * the ones that begin with a proper noun ('Fanconi anemia'). This evens that
 * out, and it is the ONLY character it is safe to touch.
 *
 * TITLE CASE WOULD BE A CLINICAL ERROR HERE. Case is already load-bearing in
 * these names: gene symbols (BRCA1), chemical and immunological prefixes
 * (acetyl-CoA, hyper-IgE, hyper-IgM, glutaryl-CoA), and eponyms
 * (Laurin-Sandrow, Noonan, Mallory). Capitalising every word gives
 * 'Thanatophoric Dysplasia Type 1', and any implementation that also lowercases
 * the rest of a word turns CoA into Coa.
 *
 * Measured across all 8,117 distinct titles in the 2026-09-20 release:
 *   * 0 titles are shaped like mtDNA / tRNA / cAMP, where the first character's
 *     case is itself semantic — that is the one family this would break, and
 *     GenCC contains none of it;
 *   * internal capitals are untouched, so acetyl-CoA → Acetyl-CoA;
 *   * a title already starting uppercase is returned byte-identical;
 *   * a title starting with a digit is left alone, so '3-methylglutaconic
 *     aciduria' does not become '3-Methylglutaconic';
 *   * 0 labels change sort position.
 *
 * NOT special-cased: 17 titles where MONDO itself lowercased a gene symbol
 * ('gnb5-related …' → 'Gnb5-related …' rather than 'GNB5-related …'). Fixing
 * that would mean inferring a gene symbol out of free text, which is the
 * hazard class this feature already refuses on the genotype record itself
 * (see geneLookupKey). 'Gnb5-related' is no less correct than the
 * 'gnb5-related' upstream gives us; a wrong guess would be.
 */
function sentenceCase(s) {
  if (typeof s !== 'string' || !s) return s;
  // Only a letter is promoted. Digits and symbols are returned untouched,
  // which is what keeps chemical prefixes correct.
  return /^[a-z]/.test(s) ? s[0].toUpperCase() + s.slice(1) : s;
}

/**
 * The row heading: MONDO's canonical title, which retires GenCC's shouty OMIM
 * strings ('THROMBOCYTHEMIA 3; THCYT3' → 'Thrombocythemia 3'). Populated on
 * every row of the release. The submitted title is never lost — it is the
 * catalogue sub-heading inside the expansion, and it stays VERBATIM there
 * because that line is provenance: it shows exactly the string the submitter's
 * catalogue carries, shouting and all.
 *
 * Deliberately NOT the same rule as diseaseLabelOf, which keeps the submitted
 * title because `diseases[]` is keyed per submitted curie.
 */
function groupLabelOf(a) {
  const d = a?.disease || {};
  return sentenceCase(
    d.title || d.submittedAsTitle || d.submittedAs || d.mondo || 'Unnamed'
  );
}

const bySubmitterName = (x, y) =>
  rankOf(x.classification) - rankOf(y.classification) ||
  String(x.submitter).localeCompare(String(y.submitter));

/**
 * One entry per classification PRESENT, with its count — never a fixed strip
 * of eight with zeros. GenCC renders all nine with 0s because its grid is
 * compared ACROSS genes; on one gene a zero whose meaning depends on a column
 * header we cannot fit is noise.
 *
 * @returns {Array<{classification: string, count: number, offScale: boolean}>}
 */
export function tallyOf(submitters = []) {
  const counts = new Map();
  for (const s of submitters) {
    const c = s?.classification;
    if (!c) continue;
    counts.set(c, (counts.get(c) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([classification, count]) => ({
      classification,
      count,
      offScale: isUngradedScale(classification)
    }))
    .sort(
      (a, b) =>
        // Off-scale forced LAST regardless of rank. RANK seats Supportive at 3,
        // above Limited, so leaving it in rank order would have position
        // asserting Supportive > Limited — the exact ladder reading
        // isUngradedScale exists to forbid.
        Number(a.offScale) - Number(b.offScale) ||
        rankOf(a.classification) - rankOf(b.classification)
    );
}

/**
 * A STRONG from one submitter and a WEAK from a DIFFERENT one — what licenses
 * the plural in "Submitters disagree". 1,339 of the 1,365 conflicting groups.
 */
function submittersDisagreeOn(submitters) {
  const strongBy = new Set();
  const weakBy = new Set();
  for (const s of submitters) {
    if (STRONG.has(s?.classification)) strongBy.add(s?.submitter);
    if (WEAK.has(s?.classification)) weakBy.add(s?.submitter);
  }
  for (const name of strongBy) {
    for (const other of weakBy) if (other !== name) return true;
  }
  return false;
}

/**
 * The other 26 groups: ONE submitter filing both sides. POMC/OMIM:601665 is
 * Ambry Genetics saying Strong AND Limited.
 *
 * Today every submitter line is on screen, so a clinician can see it is one
 * lab twice. Collapsed behind a summary, "Submitters disagree" would be
 * plainly false — so the row says who, by name, instead.
 */
function selfContradictionOf(submitters) {
  const byName = new Map();
  for (const s of submitters) {
    const name = s?.submitter;
    if (!name) continue;
    if (!byName.has(name)) byName.set(name, new Set());
    byName.get(name).add(s.classification);
  }
  for (const [submitter, set] of byName) {
    const cs = [...set];
    if (cs.some((c) => STRONG.has(c)) && cs.some((c) => WEAK.has(c))) {
      return {
        submitter,
        classifications: cs.sort((a, b) => rankOf(a) - rankOf(b))
      };
    }
  }
  return null;
}

function buildDiseaseGroups(assertions) {
  const splitCuries = splitCuriesOf(assertions);
  const byGroup = new Map();

  for (const a of assertions) {
    const key = equivalentKeyOf(a, splitCuries);
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        key,
        mondo: a?.disease?.mondo ?? null,
        label: groupLabelOf(a),
        codes: [],
        sourceMap: new Map(),
        submitters: []
      });
    }
    const g = byGroup.get(key);
    const code = submittedCodeOf(a);
    if (!g.sourceMap.has(code)) {
      g.sourceMap.set(code, {
        code,
        title: a?.disease?.submittedAsTitle ?? null,
        submitters: []
      });
      g.codes.push(code);
    }
    g.sourceMap.get(code).submitters.push(a);
    g.submitters.push(a);
  }

  const groups = [...byGroup.values()].map((g) => {
    g.submitters.sort(bySubmitterName);
    const sources = g.codes.map((c) => {
      const s = g.sourceMap.get(c);
      s.submitters.sort(bySubmitterName);
      return s;
    });
    const classifications = [
      ...new Set(g.submitters.map((s) => s.classification))
    ];
    const hasStrong = classifications.some((c) => STRONG.has(c));
    const hasWeak = classifications.some((c) => WEAK.has(c));
    return {
      key: g.key,
      mondo: g.mondo,
      label: g.label,
      merged: g.codes.length > 1,
      codes: g.codes,
      sources,
      submitters: g.submitters,
      classifications,
      tally: tallyOf(g.submitters),
      // The SAME rule diseases[] uses, so nothing downstream learns a new one.
      conflicting: hasStrong && hasWeak,
      submittersDisagree: submittersDisagreeOn(g.submitters),
      selfContradiction: selfContradictionOf(g.submitters),
      hasUngraded: g.submitters.some((s) => isUngradedScale(s.classification)),
      // ORDERING ONLY — same name, same discipline as on diseases[]. There is
      // deliberately no `grade` and no `classification` scalar on a group.
      strongest: g.submitters[0]?.classification ?? null
    };
  });

  groups.sort(
    (a, b) =>
      rankOf(a.strongest) - rankOf(b.strongest) ||
      a.label.localeCompare(b.label)
  );
  return groups;
}

/**
 * Which rows are open when the block first renders.
 *
 * Pure, so the thresholds are asserted without a render. Measured against the
 * live release: this opens 4,588 of 6,108 genes (75.1%) in full, so the quiet
 * majority reads exactly as it did before the accordion existed — grade,
 * submitter, mode of inheritance and date all on screen with no click.
 *
 * Above that threshold only the rows where submitters disagree are open,
 * because that is the one thing a clinician must not have to discover.
 */
export function defaultExpandedKeys(diseaseGroups = []) {
  if (!diseaseGroups.length) return [];
  // Never make someone click to see the only answer there is.
  if (diseaseGroups.length === 1) return [diseaseGroups[0].key];
  const submissions = diseaseGroups.reduce(
    (n, g) => n + g.submitters.length,
    0
  );
  if (diseaseGroups.length <= 3 && submissions <= 6)
    return diseaseGroups.map((g) => g.key);
  return diseaseGroups.filter((g) => g.conflicting).map((g) => g.key);
}

// A citation list can run to 82 entries (SCN1A). The cap bounds the URL, and
// the count is always rendered from the FULL list so the truncation is never
// silent.
const MAX_PMIDS_IN_URL = 50;

/**
 * GenCC's `pmids` is a comma-separated list of bare ids in this release, but
 * historic rows carry a 'PMID:' prefix, so both are accepted.
 */
export function pmidList(pmids) {
  if (typeof pmids !== 'string') return [];
  return [
    ...new Set(
      pmids
        .split(/[,;\s]+/)
        .map((p) => p.replace(/^PMID:?/i, '').trim())
        .filter((p) => /^\d+$/.test(p))
    )
  ];
}

/** A PubMed search over the cited ids. Verified: a comma-separated id list in
 *  `term` resolves to exactly those records. */
export function pubmedSearchUrl(ids) {
  if (!Array.isArray(ids) || !ids.length) return null;
  const capped = ids.slice(0, MAX_PMIDS_IN_URL);
  return `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(
    capped.join(',')
  )}`;
}

/** The gene's own page on GenCC. Verified live: search.thegencc.org 301s here,
 *  so the canonical host is used directly rather than through a redirect. */
export function genccGeneUrl(hgncId) {
  const id = typeof hgncId === 'string' ? hgncId.trim() : '';
  return /^HGNC:\d+$/.test(id)
    ? `https://thegencc.org/genes/${encodeURIComponent(id)}`
    : null;
}

/** Key an assertion to its disease, preferring the submitted-as code. */
function diseaseKeyOf(a) {
  const d = a?.disease || {};
  return d.submittedAs || d.mondo || d.code || '(unknown)';
}

function diseaseLabelOf(a) {
  const d = a?.disease || {};
  return d.submittedAsTitle || d.title || d.submittedAs || d.mondo || 'Unnamed';
}

/**
 * Group a gene's assertions into one row per disease, each carrying every
 * submitter.
 *
 * @param {object} response the /gencc/by-gene payload
 * @returns {{
 *   state: string,
 *   reason: string|null,
 *   datasetVersion: string|null,
 *   datasetDownloadedAt: string|null,
 *   diseases: Array<{
 *     key: string, label: string, code: string|null, mondo: string|null,
 *     submitters: Array<object>, classifications: string[],
 *     conflicting: boolean, strongest: string|null
 *   }>
 * }}
 */
export function buildGeneValidity(response, { geneIdentified = true } = {}) {
  const base0 = emptyBase(response);
  // Checked FIRST. Without an approved symbol there is no question to ask, so
  // any other state here would be an answer we did not earn.
  if (!geneIdentified) {
    return {
      ...base0,
      state: GENE_VALIDITY_STATE.UNIDENTIFIED_GENE,
      reason: null
    };
  }

  const available = response?.available === true;
  const base = emptyBase(response);

  if (!available) {
    return {
      ...base,
      state: GENE_VALIDITY_STATE.UNAVAILABLE,
      // `reason` comes from the backend's availability contract
      // (disabled | unreachable | vocabulary-empty). Carried through so the
      // copy can say WHY rather than just "unavailable".
      reason: response?.reason ?? null
    };
  }

  const assertions = Array.isArray(response?.assertions)
    ? response.assertions
    : [];
  if (!assertions.length) {
    return { ...base, state: GENE_VALIDITY_STATE.NOT_CURATED, reason: null };
  }

  const byDisease = new Map();
  for (const a of assertions) {
    const key = diseaseKeyOf(a);
    if (!byDisease.has(key)) {
      byDisease.set(key, {
        key,
        label: diseaseLabelOf(a),
        code: a?.disease?.submittedAs ?? null,
        mondo: a?.disease?.mondo ?? null,
        submitters: []
      });
    }
    byDisease.get(key).submitters.push(a);
  }

  const diseases = [...byDisease.values()].map((d) => {
    // Submitters ordered strongest-first so the row reads consistently; the
    // order is presentational and carries no verdict.
    d.submitters.sort(
      (x, y) =>
        rankOf(x.classification) - rankOf(y.classification) ||
        String(x.submitter).localeCompare(String(y.submitter))
    );
    const classifications = [
      ...new Set(d.submitters.map((s) => s.classification))
    ];
    const hasStrong = classifications.some((c) => STRONG.has(c));
    const hasWeak = classifications.some((c) => WEAK.has(c));
    return {
      ...d,
      classifications,
      // A disagreement worth flagging is one that crosses the evidence line.
      // Definitive-vs-Strong is not flagged; Definitive-vs-Refuted is.
      conflicting: hasStrong && hasWeak,
      strongest: d.submitters[0]?.classification ?? null
    };
  });

  diseases.sort(
    (a, b) =>
      rankOf(a.strongest) - rankOf(b.strongest) ||
      a.label.localeCompare(b.label)
  );

  const diseaseGroups = buildDiseaseGroups(assertions);

  return {
    ...base,
    gene: geneOf(assertions),
    // Widened to the GROUPS, which is a strict superset of the per-curie rows
    // (see buildDiseaseGroups). On the live 2026-09-20 release that raises 84
    // genes from graded to conflicting and lowers none.
    state: diseaseGroups.some((g) => g.conflicting)
      ? GENE_VALIDITY_STATE.CONFLICTING
      : GENE_VALIDITY_STATE.GRADED,
    reason: null,
    diseases,
    diseaseGroups,
    totals: {
      diseases: diseaseGroups.length,
      // Submissions and submitters BOTH, deliberately: one submitter can file
      // twice against the same pair (366 groups do), so a submission count
      // alone reads as that many independent opinions.
      submissions: assertions.length,
      submitters: new Set(assertions.map((a) => a?.submitter).filter(Boolean))
        .size,
      conflicting: diseaseGroups.filter((g) => g.conflicting).length,
      hasUngraded: diseaseGroups.some((g) => g.hasUngraded)
    }
  };
}

/**
 * Copy for the empty states. Written out here rather than inline so the
 * wording is reviewable and testable — the brief specifies the not-curated
 * sentence almost verbatim, because the whole point is that absence of a GenCC
 * submission is not evidence against an association.
 */
export function emptyStateCopy(state, reason) {
  if (state === GENE_VALIDITY_STATE.UNIDENTIFIED_GENE) {
    return {
      heading: 'Gene not identified',
      body:
        "This record's gene is not recorded as an approved HGNC symbol, so " +
        'GenCC validity cannot be looked up. This says nothing about the ' +
        'evidence for the gene — only that it could not be matched. Some ' +
        'integrations record genes outside HGNC nomenclature by design.'
    };
  }
  if (state === GENE_VALIDITY_STATE.NOT_CURATED) {
    return {
      heading: 'Not curated by GenCC',
      body:
        'No GenCC submission for this gene. This is not evidence against an ' +
        'association — it means no member of the Gene Curation Coalition has ' +
        'published a validity assessment for it.'
    };
  }
  if (reason === 'disabled') {
    return {
      heading: 'GenCC is switched off',
      body:
        'Gene–disease validity is disabled for this deployment in ' +
        'Settings → Terminology. No conclusion about this gene can be drawn ' +
        'from its absence here.'
    };
  }
  return {
    heading: 'GenCC could not be reached',
    body:
      'Validity data is unavailable right now, so nothing is being shown for ' +
      'this gene. This is not a statement about the evidence — try again, or ' +
      'check Settings → Terminology.'
  };
}
